/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

const CustomStatusDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOptionClick = (status) => {
    onChange({ target: { value: status } });
    setIsOpen(false);
  };

  // Get status text for display
  const getStatusText = () => {
    switch (value) {
      case "open":
        return "Public";
      case "unlisted":
        return "Unlisted";
      case "closed":
        return "Closed";
      default:
        return "Public";
    }
  };

  // Get indicator color based on current status
  const getIndicatorColor = () => {
    switch (value) {
      case "open":
        return "#94D365"; // Green for public
      case "unlisted":
        return "#F5A623"; // Orange/yellow for unlisted
      case "closed":
        return "#AAAAAA"; // Grey for closed
      default:
        return "#94D365";
    }
  };

  return (
    <div
      ref={dropdownRef}
      style={{
        position: "relative",
        width: "150px",
        userSelect: "none",
      }}
    >
      {/* Selected option display */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: "flex",
          alignItems: "center",
          padding: "0 10px",
          height: "40px",
          borderRadius: "4px",
          border: "1px solid #ddd",
          backgroundColor: "#fff",
          cursor: "pointer",
          fontSize: "14px",
          position: "relative",
        }}
      >
        <div
          style={{
            width: "12px",
            height: "12px",
            borderRadius: "50%",
            background: getIndicatorColor(),
            marginRight: "8px",
          }}
        />
        <div style={{ flex: 1 }}>{getStatusText()}</div>
        <div
          style={{
            width: "0",
            height: "0",
            borderLeft: "5px solid transparent",
            borderRight: "5px solid transparent",
            borderTop: "5px solid #666",
            marginLeft: "8px",
          }}
        />
      </div>

      {/* Dropdown options */}
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "45px",
            left: "0",
            width: "100%",
            backgroundColor: "#fff",
            border: "1px solid #ddd",
            borderRadius: "4px",
            boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
            zIndex: 10,
          }}
        >
          {/* Public option with special styling */}
          <div
            onClick={() => handleOptionClick("open")}
            style={{
              display: "flex",
              alignItems: "center",
              padding: "10px",
              cursor: "pointer",
              backgroundColor: value === "open" ? "#f5f5f5" : "#fff",
              borderBottom: "1px solid #eee",
            }}
          >
            <div
              style={{
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                background: "#94D365",
                marginRight: "8px",
              }}
            />
            <div
              style={{
                fontWeight: value === "open" ? "bold" : "normal",
                display: "flex",
                alignItems: "center",
              }}
            >
              Public
              <span
                style={{
                  marginLeft: "5px",
                  fontSize: "10px",
                  color: "#666",
                  backgroundColor: "#e6f7d9",
                  padding: "2px 5px",
                  borderRadius: "3px",
                }}
              >
                Visible to all
              </span>
            </div>
          </div>

          {/* Unlisted option */}
          <div
            onClick={() => handleOptionClick("unlisted")}
            style={{
              display: "flex",
              alignItems: "center",
              padding: "10px",
              cursor: "pointer",
              backgroundColor: value === "unlisted" ? "#f5f5f5" : "#fff",
              borderBottom: "1px solid #eee",
            }}
          >
            <div
              style={{
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                background: "#F5A623",
                marginRight: "8px",
              }}
            />
            <div
              style={{ fontWeight: value === "unlisted" ? "bold" : "normal" }}
            >
              Unlisted
            </div>
          </div>

          {/* Closed option */}
          <div
            onClick={() => handleOptionClick("closed")}
            style={{
              display: "flex",
              alignItems: "center",
              padding: "10px",
              cursor: "pointer",
              backgroundColor: value === "closed" ? "#f5f5f5" : "#fff",
            }}
          >
            <div
              style={{
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                background: "#AAAAAA",
                marginRight: "8px",
              }}
            />
            <div style={{ fontWeight: value === "closed" ? "bold" : "normal" }}>
              Closed
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

CustomStatusDropdown.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default CustomStatusDropdown;
